import clsx from "clsx";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import { ClockBadgeIcon, CrossIcon } from "../../Icons";
import { ResponsiveModal } from "../../ResponsiveModal";
import { Typo } from "../../Typo";
import { withSupportLink } from "./supportLink";
import { useDefaultSetPendingTranslations } from "./useDefaultSetPendingTranslations";

type DefaultSetPendingModalProps = {
  open: boolean;
  onClose: () => void;
  isMobile?: boolean;
};

export const DefaultSetPendingModal = ({
  open,
  onClose,
  isMobile = false,
}: DefaultSetPendingModalProps) => {
  const translations = useDefaultSetPendingTranslations();

  const manageButton = (
    <ButtonV2
      variant="brand"
      size="md"
      onClick={onClose}
      className={clsx(isMobile && "w-full")}
      data-testid="default_set_pending_modal_manage_button"
    >
      {translations.manage}
    </ButtonV2>
  );

  const illustration = (
    <ClockBadgeIcon
      className="h-16 w-16"
      aria-hidden="true"
      focusable="false"
    />
  );

  const copy = (
    <>
      <Typo
        element="h2"
        type={isMobile ? "headingLG" : "headingXL"}
        textColor="text-text-primary"
        className={clsx(isMobile && "mt-1")}
      >
        {translations.title}
      </Typo>
      <Typo
        element="p"
        type={isMobile ? "bodySM" : "bodyMD"}
        textColor="text-text-secondary"
      >
        {translations.lead}
      </Typo>
      <Typo
        element="p"
        type={isMobile ? "bodyXS" : "bodySM"}
        textColor={isMobile ? "text-text-tertiary" : "text-text-secondary"}
      >
        {withSupportLink(translations.help)}
      </Typo>
    </>
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={open}
        onClose={onClose}
        isFullScreen
        isDismissable={false}
        showCloseButton
        closeButtonTestId="default_set_pending_modal_close_button"
        footer={manageButton}
      >
        <div
          className="flex flex-col gap-3 pb-1 pt-2"
          role="alert"
          data-testid="default_set_pending_modal"
        >
          {illustration}
          {copy}
        </div>
      </BottomSheet>
    );
  }

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      isMobile={false}
      headerClassName="!hidden"
      footerContainerClassName="!mt-8"
      footer={<div className="flex justify-end">{manageButton}</div>}
    >
      <div
        className="flex flex-col gap-4"
        role="alert"
        data-testid="default_set_pending_modal"
      >
        <div className="flex flex-col">
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={translations.close}
              onClick={onClose}
              data-testid="default_set_pending_modal_close_button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
            >
              <CrossIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {illustration}
        </div>
        {copy}
      </div>
    </ResponsiveModal>
  );
};
